import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import Layout from '../Home'
import {
  Table,
  Button,
  Modal,
  Form,
  Switch,
  InputNumber,
  message,
  Space,
  Image,
  Popconfirm,
  Tag,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  PictureOutlined,
} from '@ant-design/icons'
import firebase from 'firebase/app'
import 'firebase/database'
import 'firebase/storage'
import imageCompression from 'browser-image-compression'

const BannerManagement = (props) => {
  const [banners, setBanners] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingBanner, setEditingBanner] = useState(null)
  const [imageUrl, setImageUrl] = useState(null)
  const [uploading, setUploading] = useState(false)

  const [form] = Form.useForm()
  const bannerRef = useRef(firebase.database().ref('/anoucement'))
  const bannerListenerRef = useRef(null)
  const fileInputRef = useRef(null)

  const setupRealtimeListener = useCallback(() => {
    bannerListenerRef.current = bannerRef.current.on('value', (snapshot) => {
      const data = snapshot.val()
      const bannersList = []
      if (data) {
        Object.keys(data).forEach((key) => {
          bannersList.push({
            key,
            ...data[key],
          })
        })
      }
      // เรียงตาม order
      bannersList.sort((a, b) => {
        const orderA = a?.order ?? 0
        const orderB = b?.order ?? 0
        return orderA - orderB
      })
      setBanners(bannersList)
      setLoading(false)
    })
  }, [])

  const fetchBanners = useCallback(async () => {
    setLoading(true)
    try {
      const snapshot = await bannerRef.current.once('value')
      const data = snapshot.val()
      const bannersList = []
      if (data) {
        Object.keys(data).forEach((key) => {
          bannersList.push({
            key,
            ...data[key],
          })
        })
      }
      // เรียงตาม order
      bannersList.sort((a, b) => {
        const orderA = a?.order ?? 0
        const orderB = b?.order ?? 0
        return orderA - orderB
      })
      setBanners(bannersList)
      setLoading(false)
    } catch (error) {
      console.error('Error fetching banners:', error)
      message.error('ไม่สามารถดึงข้อมูลได้')
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchBanners()
    setupRealtimeListener()

    return () => {
      if (bannerListenerRef.current) {
        bannerRef.current.off('value', bannerListenerRef.current)
      }
    }
  }, [fetchBanners, setupRealtimeListener])

  const handleAdd = useCallback(() => {
    setModalVisible(true)
    setEditingBanner(null)
    setImageUrl(null)
    form.resetFields()
  }, [form])

  const handleEdit = useCallback(
    (record) => {
      setModalVisible(true)
      setEditingBanner(record)
      setImageUrl(record.imageUrl || null)

      form.setFieldsValue({
        enable: record.enable ?? true,
        order: record.order ?? 0,
      })
    },
    [form]
  )

  const handleDelete = useCallback(async (key) => {
    try {
      const bannerRef = firebase.database().ref(`/anoucement/${key}`)
      const snapshot = await bannerRef.once('value')
      const bannerData = snapshot.val()

      // ลบรูปภาพจาก Storage ถ้ามี
      if (bannerData && bannerData.imageUrl) {
        try {
          const imageRef = firebase.storage().refFromURL(bannerData.imageUrl)
          await imageRef.delete()
        } catch (error) {
          console.error('Error deleting image:', error)
          // ยังคงลบข้อมูลต่อแม้ว่าลบรูปไม่สำเร็จ
        }
      }

      await bannerRef.remove()
      message.success('ลบ Banner สำเร็จ')
    } catch (error) {
      console.error('Error deleting banner:', error)
      message.error('ไม่สามารถลบ Banner ได้')
    }
  }, [])

  const handleCancel = useCallback(() => {
    setModalVisible(false)
    setEditingBanner(null)
    setImageUrl(null)
    setUploading(false)
    form.resetFields()
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [form])

  const handleImageUpload = useCallback(async (e) => {
    const file = e.target.files[0]
    if (!file) return

    if (file.size > 5 * 1024 * 1024) {
      message.error('ขนาดไฟล์ไม่ควรเกิน 5MB')
      return
    }

    setUploading(true)

    try {
      let fileToUpload = file

      // Compress image if larger than 300KB
      if (file.size > 300 * 1024) {
        const options = {
          maxSizeMB: 0.3,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        }
        fileToUpload = await imageCompression(file, options)
      }

      const timestamp = Date.now()
      const fileName = `banner-${timestamp}-${file.name}`
      const storageRef = firebase.storage().ref(`banner/${fileName}`)
      const task = storageRef.put(fileToUpload)

      task.on(
        'state_changed',
        (snapshot) => {
          // Progress tracking could be added here
        },
        (error) => {
          console.error('Upload error:', error)
          message.error('อัพโหลดรูปภาพผิดพลาด')
          setUploading(false)
        },
        async () => {
          const downloadURL = await storageRef.getDownloadURL()
          setImageUrl(downloadURL)
          setUploading(false)
          message.success('อัพโหลดรูปภาพสำเร็จ')
        }
      )
    } catch (error) {
      console.error('Error uploading image:', error)
      message.error('อัพโหลดรูปภาพผิดพลาด')
      setUploading(false)
    }
  }, [])

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields()

      if (!imageUrl && !editingBanner) {
        message.error('กรุณาอัพโหลดรูปภาพ')
        return
      }

      const bannerData = {
        enable: values.enable ?? true,
        order: values.order ?? 0,
        userId: -1, // แสดงให้ทุกคน
        imageUrl: imageUrl || editingBanner?.imageUrl,
      }

      if (editingBanner) {
        // อัพเดท
        await firebase
          .database()
          .ref(`/anoucement/${editingBanner.key}`)
          .update(bannerData)
        message.success('อัพเดท Banner สำเร็จ')
      } else {
        // เพิ่มใหม่
        await firebase.database().ref('/anoucement').push(bannerData)
        message.success('เพิ่ม Banner สำเร็จ')
      }

      handleCancel()
    } catch (error) {
      console.error('Error saving banner:', error)
      if (error.errorFields) {
        message.error('กรุณากรอกข้อมูลให้ครบถ้วน')
      } else {
        message.error('บันทึกข้อมูลผิดพลาด')
      }
    }
  }, [form, imageUrl, editingBanner, handleCancel])

  const columns = useMemo(
    () => [
      {
        title: 'ลำดับ',
        dataIndex: 'order',
        key: 'order',
        width: 80,
        sorter: (a, b) => (a.order || 0) - (b.order || 0),
      },
      {
        title: 'รูปภาพ',
        dataIndex: 'imageUrl',
        key: 'imageUrl',
        width: 150,
        render: (url) => (
          <Image
            src={url}
            alt="Banner"
            width={100}
            height={60}
            style={{ objectFit: 'cover', borderRadius: 4 }}
            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN"
          />
        ),
      },
      {
        title: 'ผู้ใช้',
        dataIndex: 'userId',
        key: 'userId',
        width: 150,
        render: (userId) => {
          if (userId === -1 || userId === '-1') {
            return <Tag color="blue">ทุกคน</Tag>
          } else {
            return <Tag color="orange">User ID: {userId}</Tag>
          }
        },
      },
      {
        title: 'สถานะ',
        dataIndex: 'enable',
        key: 'enable',
        width: 100,
        render: (enable) => (
          <Tag color={enable ? 'green' : 'red'}>{enable ? 'เปิดใช้งาน' : 'ปิดใช้งาน'}</Tag>
        ),
      },
      {
        title: 'จัดการ',
        key: 'action',
        width: 150,
        fixed: 'right',
        render: (_, record) => (
          <Space>
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => handleEdit(record)}
            >
              แก้ไข
            </Button>
            <Popconfirm
              title="คุณแน่ใจหรือไม่ที่จะลบ Banner นี้?"
              onConfirm={() => handleDelete(record.key)}
              okText="ใช่"
              cancelText="ไม่"
            >
              <Button type="danger" icon={<DeleteOutlined />} size="small">
                ลบ
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [handleEdit, handleDelete]
  )

  return (
    <Layout
      className="h-100"
      {...props}
      render={(layoutProps) => (
        <div className="w-100 p-4 h-100">
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
            <h3 className="h3" style={{ fontWeight: 'bold' }}>
              จัดการ Banner Mobile
            </h3>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              เพิ่ม Banner
            </Button>
          </div>

          <Table
            columns={columns}
            dataSource={banners}
            loading={loading}
            scroll={{ x: 1000 }}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `ทั้งหมด ${total} รายการ`,
            }}
          />

          <Modal
            title={editingBanner ? 'แก้ไข Banner' : 'เพิ่ม Banner ใหม่'}
            visible={modalVisible}
            onCancel={handleCancel}
            onOk={handleSubmit}
            width={800}
            okText="บันทึก"
            cancelText="ยกเลิก"
            confirmLoading={uploading}
          >
            <Form
              form={form}
              layout="vertical"
              initialValues={{
                enable: true,
                order: 0,
              }}
            >
              <Form.Item label="อัพโหลดรูปภาพ" required>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png, image/jpeg, image/jpg"
                    onChange={handleImageUpload}
                    disabled={uploading}
                    style={{ display: 'none' }}
                    id="banner-upload-input"
                  />
                  <Button
                    icon={<PictureOutlined />}
                    loading={uploading}
                    disabled={uploading}
                    style={{ marginBottom: 10 }}
                    type="button"
                    onClick={() => {
                      if (!uploading && fileInputRef.current) {
                        fileInputRef.current.click()
                      }
                    }}
                  >
                    {uploading ? 'กำลังอัพโหลด...' : 'เลือกไฟล์'}
                  </Button>
                  {(imageUrl || editingBanner?.imageUrl) && (
                    <div style={{ marginTop: 10 }}>
                      <Image
                        src={imageUrl || editingBanner?.imageUrl}
                        alt="Banner preview"
                        width={200}
                        style={{ borderRadius: 4 }}
                      />
                    </div>
                  )}
                </div>
              </Form.Item>

              <Form.Item label="เปิดใช้งาน" name="enable" valuePropName="checked">
                <Switch />
              </Form.Item>

              <Form.Item
                label="ลำดับการแสดง"
                name="order"
                rules={[{ required: true, message: 'กรุณากรอกลำดับ' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Form>
          </Modal>
        </div>
      )}
    />
  )
}

export default BannerManagement